import type { CompanyMonthSnapshot } from "@/company/core/types";
import { ProgressDonut } from "@/company/components/ProgressDonut";
import { gaugePercentage } from "@/company/components/TemperatureGauge";
import { formatMetricForCell } from "@/grid/core/format-metric";
import { cn } from "@/lib/cn";

interface CompanyMonthChartProps {
  snapshots: CompanyMonthSnapshot[];
  className?: string;
}

function salesMetric(snapshot: CompanyMonthSnapshot) {
  return snapshot.metrics.find((metric) => metric.metricId === "sales");
}

export function CompanyMonthChart({ snapshots, className }: CompanyMonthChartProps) {
  if (snapshots.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-[var(--color-text-muted)]">
        No monthly data for this period
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <div className={snapshots.length > 4 ? "overflow-x-auto pb-2" : undefined}>
        <div
          className="grid min-h-[220px] min-w-max gap-6"
          style={{
            gridTemplateColumns: `repeat(${snapshots.length}, minmax(${snapshots.length > 4 ? "88px" : "0"}, 1fr))`,
          }}
        >
          {snapshots.map((snapshot) => {
            const sales = salesMetric(snapshot);
            if (!sales) return null;

            const percentage = gaugePercentage(sales.actual, sales.target);
            const monthGoal = sales.monthGoal ?? sales.target;

            return (
              <div key={snapshot.monthStart} className="flex min-w-0 flex-col items-center">
                <p className="mb-3 text-sm font-semibold text-[var(--color-text)]">
                  {snapshot.label}
                </p>

                <ProgressDonut percentage={percentage} />

                <p className="mt-3 text-2xl font-bold tabular-nums leading-none text-[var(--color-text)]">
                  {percentage}%
                </p>

                <p className="mt-2 text-center text-[11px] tabular-nums text-[var(--color-text-muted)]">
                  {formatMetricForCell(sales.actual, "currency")}
                  {monthGoal != null ? <> / {formatMetricForCell(monthGoal, "currency")}</> : null}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
